import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Divider, Modal, Skeleton, Typography } from "@mui/material";

import { Service } from "../types/service.type";

import AlertFigure from "../assets/images/cancel-alert.png";
import { useState } from "react";
import { ServiceStatusEnum } from "../enum/service.enum";

const ServiceApprovalStatus = ({ service, isLoading }: { service?: Service; isLoading: boolean }) => {
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const { t } = useTranslation("goService", { keyPrefix: "serviceApprovalStatus" });

  const serviceStatus =
    service?.approvedByAdmin === null ? "underReview" : service?.approvedByAdmin ? service?.status : "rejected";

  const handleRejectionModal = () => setIsRejectionModalOpen(true);

  if (isLoading) return <Skeleton variant="rounded" width={50} height={16} />;

  if (!service) return null;

  return (
    <>
      <Chip
        label={t(serviceStatus)}
        onClick={handleRejectionModal}
        clickable={serviceStatus === "rejected"}
        sx={styles.chip(serviceStatus as ServiceStatusEnum)}
      />
      {serviceStatus === "rejected" && (
        <ServiceRejectionModal
          rejectReason={service?.rejectReason}
          open={isRejectionModalOpen}
          closeModal={() => setIsRejectionModalOpen(false)}
        />
      )}
    </>
  );
};

export default ServiceApprovalStatus;

const ServiceRejectionModal = ({
  rejectReason,
  open,
  closeModal,
}: {
  rejectReason: string | null;
  open: boolean;
  closeModal: () => void;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceApprovalStatus" });

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.modalContent}>
          <Box component="img" src={AlertFigure} alt="service-request-submitted" sx={styles.alertFigure} />
          <Box sx={styles.modalTextContainer}>
            <Typography sx={styles.title}>{t("yourServiceRejected")}</Typography>
            <Typography sx={styles.description}>{t("unfortunatelyYourServiceDidNotPassOurReviewProcess")}</Typography>
            {rejectReason && (
              <>
                <Typography sx={styles.description}>{t("reasons")}</Typography>
                <Typography sx={styles.description}>{rejectReason}</Typography>
              </>
            )}
          </Box>
          <Divider orientation="horizontal" flexItem sx={styles.divider} />
          <Box sx={styles.buttonsContainer}>
            <Button variant="outlined-md" onClick={closeModal}>
              {t("cancel")}
            </Button>
            <Button variant="primary-md" onClick={closeModal}>
              {t("iUnderstand")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  modalContainer: {
    width: "770px",
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    background: "#FFF",
    boxShadow: "0px 4px 50px 0px rgba(0, 0, 0, 0.08), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
    padding: "40px 24px 16px",
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
  },
  modalTextContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    paddingBottom: "40px",
  },
  alertFigure: {
    width: "100px",
    height: "100px",
  },
  title: {
    color: "#000",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  description: {
    color: "var(--Gray-Gray-700, #5E6278)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  divider: {
    mx: "-32px",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
    width: "100%",
  },
  chipMainStyle: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    padding: "2px 6px",
    height: "fit-content",
    "& .MuiChip-label": {
      padding: 0,
    },
  },
  chip: (status: "rejected" | "underReview" | "approved" | ServiceStatusEnum) => {
    if (status === "rejected") {
      return {
        color: "#EF4444",
        backgroundColor: "#FDE8E8",
        borderColor: "#F9B9B9",
        ...styles.chipMainStyle,
      };
    }

    if (status === "underReview") {
      return {
        color: "#F97316",
        backgroundColor: "#FEF0E6",
        borderColor: "#FCD3B5",
        ...styles.chipMainStyle,
      };
    }

    if (status === "approved") {
      return {
        color: "#076D0D",
        backgroundColor: "#E7FDE8",
        borderColor: "#7EF785",
        ...styles.chipMainStyle,
      };
    }

    if (status === "published") {
      return {
        color: "#076D0D",
        backgroundColor: "#E7FDE8",
        borderColor: "#7EF785",
        ...styles.chipMainStyle,
      };
    }

    if (status === "draft") {
      return {
        color: "#EF4444",
        backgroundColor: "#FDE8E8",
        borderColor: "#F9B9B9",
        ...styles.chipMainStyle,
      };
    }
  },
};
